import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isAxiosError } from 'axios';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { getNorms } from '../../store/selectors/user.selector';
import { ProjectGeometry } from '../../types/drawing';
import { Project } from '../../types/project';
import { buildPlanModel, planImage } from '../new-project-cad/cadProjectRequest';
import { fromDrawingDocument, toDrawingDocument } from '../new-project-cad/drawingDocument';
import { diffDrawings, hasChanges, lightsForApply, meshesForVersion, planLamps } from '../new-project-cad/planEditing';
import { CadPlan } from '../new-project-cad/useCadState';
import { projectGeometryApi } from './drawingsApi';
import { renderPlanThumbnail } from './planThumbnail';
import { GEOMETRY_LOCK_MESSAGE } from './projectGeometry';

/** Where the change goes: the drawing the project came from, or a new drawing. */
export type DrawingTarget = { kind: 'existing' } | { kind: 'new'; name: string };

interface UpdateProjectDialogProps {
  projectId: number;
  drawingName: string;
  /** The drawing was saved from somewhere else meanwhile: the change can only go to a new one. */
  existingBlocked: boolean;
  /**
   * The editor is on this project's version while the drawing has changed since (another project
   * saved its change to it): updating the drawing would replace that change.
   */
  drawingMovedOn: boolean;
  /** The drawing as it is in the editor. */
  plan: CadPlan & { imageScale: number | null };
  referenceImageUrl: string | null;
  /** Saves the change first, so the project gets what is saved; resolves to the drawing's id. */
  saveDrawing: (target: DrawingTarget) => Promise<number | null>;
  onCancel: () => void;
  onUpdated: () => void;
}

type Step = 'drawing' | 'model' | 'upload';

/**
 * Rebuilds a project's 3D model from the drawing. All versions share the model: each keeps its
 * lighting settings and luminaires (those left off the plan can go, those on a surface whose height
 * changed move with it). Not possible once a calculation has started.
 */
export function UpdateProjectDialog({
  projectId,
  drawingName,
  existingBlocked,
  drawingMovedOn,
  plan,
  referenceImageUrl,
  saveDrawing,
  onCancel,
  onUpdated,
}: UpdateProjectDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const norms = useAppSelector(getNorms);
  const [project, setProject] = useState<Project | null>(null);
  const [geometry, setGeometry] = useState<ProjectGeometry | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [removeLamps, setRemoveLamps] = useState(true);
  const [step, setStep] = useState<Step | null>(null);
  const [target, setTarget] = useState<DrawingTarget['kind']>(existingBlocked || drawingMovedOn ? 'new' : 'existing');
  const [newName, setNewName] = useState(() => t('drawings.copyName', { name: drawingName }));

  useEffect(() => {
    if (existingBlocked) setTarget('new');
  }, [existingBlocked]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([projectGeometryApi.project(projectId), projectGeometryApi.get(projectId)])
      .then(([loadedProject, loadedGeometry]) => {
        if (cancelled) return;
        setProject(loadedProject);
        setGeometry(loadedGeometry);
      })
      .catch(() => !cancelled && setLoadFailed(true));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // The drawing the project's model was built from: what the changes are counted against.
  const built = useMemo(() => fromDrawingDocument(geometry?.drawing ?? null), [geometry]);
  const applied = useMemo(() => built?.polygons ?? [], [built]);
  const origin = useMemo<[number, number]>(() => built?.origin ?? [0, 0], [built]);
  const diff = useMemo(() => diffDrawings(applied, plan.polygons), [applied, plan.polygons]);
  const lampPlans = useMemo(
    () =>
      (project?.versions ?? []).map((version) => ({
        version,
        lamps: planLamps(version.lights, applied, plan.polygons, plan.metersPerUnit, origin),
      })),
    [project, applied, plan.polygons, plan.metersPerUnit, origin],
  );
  const removable = lampPlans.reduce((n, p) => n + p.lamps.remove.length, 0);
  const lifted = lampPlans.reduce((n, p) => n + p.lamps.lift.length, 0);
  const lock = geometry?.lock ?? null;
  const busy = step !== null;
  const ready = !!project && !!geometry && !!built;
  const changes = hasChanges(diff);
  const canApply = ready && changes && !lock && !busy && (target === 'existing' || !!newName.trim());

  const apply = async () => {
    if (!project || !built) return;
    try {
      setStep('drawing');
      const drawingId = await saveDrawing(
        target === 'existing' ? { kind: 'existing' } : { kind: 'new', name: newName.trim() },
      );
      if (drawingId === null) {
        dispatch(toastReducer.actions.showToast({ message: 'drawings.saveChangeFailed', type: 'error' }));
        setStep(null);
        return;
      }
      setStep('model');
      const image = await planImage(referenceImageUrl, plan.imageScale, plan.metersPerUnit);
      // Same origin as when the project was created: the model, and the luminaires on it, stay put.
      const { model, meshNames } = await buildPlanModel(plan, origin);
      const update = {
        drawing: toDrawingDocument(plan, {
          imageScale: image ? plan.imageScale : null,
          origin,
          meshNames: Object.fromEntries(plan.polygons.map((p, index) => [p.id, meshNames[index]])),
        }),
        versions: lampPlans.map(({ version, lamps }) => ({
          versionId: version.id,
          meshes: meshesForVersion(plan.polygons, meshNames, applied, version.meshes, norms),
          lights: lightsForApply(version.lights, lamps, removeLamps),
        })),
        referenceImageBase64: image?.base64 ?? null,
        modelScale: image?.modelScale ?? null,
        sourceDrawingId: target === 'new' ? drawingId : null,
        previewImageBase64: renderPlanThumbnail(plan.polygons),
      };
      setStep('upload');
      await projectGeometryApi.update(projectId, update, model);
      dispatch(toastReducer.actions.showToast({ message: 'drawings.projectUpdated', type: 'success' }));
      onUpdated();
    } catch (error) {
      console.error('Updating the project failed:', error);
      const conflict = isAxiosError(error) && error.response?.status === 409;
      dispatch(
        toastReducer.actions.showToast({
          message: conflict ? 'drawings.updateBlocked' : 'drawings.updateFailed',
          type: 'error',
        }),
      );
      setStep(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="w-[440px] max-w-[calc(100vw-32px)] rounded-2xl bg-[#232323] border border-white/10 shadow-2xl p-6 text-gray-300"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-white mb-1">{t('drawings.updateTitle')}</h2>
        {project && <p className="text-xs text-gray-500 mb-4 truncate">{project.name}</p>}

        {loadFailed ? (
          <p className="text-sm text-red-400">{t('drawings.loadFailed')}</p>
        ) : !project || !geometry ? (
          <p className="text-sm text-gray-400">{t('common.loading')}</p>
        ) : !built ? (
          <p className="text-sm text-gray-400">{t('drawings.notFromDrawing')}</p>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            {lock && <p className="text-orange-300">{t(GEOMETRY_LOCK_MESSAGE[lock])}</p>}
            {changes ? (
              <div className="grid grid-cols-3 gap-2 text-center">
                <Count value={diff.added.length} label={t('drawings.added')} tone="text-[#B3EC49]" />
                <Count value={diff.changed.length} label={t('drawings.changed')} tone="text-sky-300" />
                <Count value={diff.deleted.length} label={t('drawings.removed')} tone="text-orange-300" />
              </div>
            ) : (
              <p className="text-gray-400">{t('drawings.noChanges')}</p>
            )}
            {project.versions.length > 1 && (
              <p className="text-gray-400">{t('drawings.allVersions', { count: project.versions.length })}</p>
            )}
            {removable > 0 && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={removeLamps}
                  onChange={(e) => setRemoveLamps(e.target.checked)}
                  className="mt-0.5 accent-[#B3EC49]"
                />
                <span>{t('drawings.removeLamps', { count: removable })}</span>
              </label>
            )}
            {lifted > 0 && <p className="text-gray-400">{t('drawings.liftedLamps', { count: lifted })}</p>}
            {changes && !lock && (
              <div className="mt-2 flex flex-col gap-2" role="radiogroup" aria-label={t('drawings.saveChangeTo')}>
                <p className="text-xs text-gray-400">{t('drawings.saveChangeTo')}</p>
                <TargetOption
                  selected={target === 'existing'}
                  disabled={existingBlocked || busy}
                  onSelect={() => setTarget('existing')}
                  title={t('drawings.updateExisting')}
                  hint={t(
                    existingBlocked
                      ? 'drawings.updateExistingBlocked'
                      : drawingMovedOn
                        ? 'drawings.updateExistingMovedOn'
                        : 'drawings.updateExistingHint',
                    { name: drawingName },
                  )}
                />
                <TargetOption
                  selected={target === 'new'}
                  disabled={busy}
                  onSelect={() => setTarget('new')}
                  title={t('drawings.createNew')}
                  hint={t('drawings.createNewHint', { name: drawingName })}
                />
                {target === 'new' && (
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    disabled={busy}
                    maxLength={255}
                    aria-label={t('drawings.newDrawingName')}
                    className="h-9 rounded-lg bg-black border border-black focus:border-lime-400 focus:ring-2 focus:ring-lime-400/30 text-sm px-3 text-white outline-none"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {step && <p className="mt-4 text-xs text-[#B3EC49]">{t(`drawings.step.${step}`)}</p>}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="h-9 px-4 rounded-full text-xs font-medium border border-white/20 text-gray-200 hover:border-white/40 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={apply}
            disabled={!canApply}
            className="h-9 px-4 rounded-full text-xs font-medium bg-lime-400 text-black hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('drawings.update')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TargetOption({
  selected,
  disabled,
  onSelect,
  title,
  hint,
}: {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  title: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`flex items-start gap-3 rounded-lg p-3 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        selected ? 'bg-lime-400/10 ring-1 ring-lime-400' : 'bg-[#2c2c2c] hover:bg-[#333]'
      }`}
    >
      <span
        className={`mt-0.5 w-3.5 h-3.5 rounded-full border shrink-0 ${
          selected ? 'border-[#B3EC49] bg-[#B3EC49] shadow-[inset_0_0_0_3px_#2c2c2c]' : 'border-gray-500'
        }`}
      />
      <span className="min-w-0">
        <span className="block text-sm text-white">{title}</span>
        <span className="block text-xs text-gray-400">{hint}</span>
      </span>
    </button>
  );
}

function Count({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div className="rounded-lg bg-[#2c2c2c] py-2">
      <div className={`text-lg font-semibold ${tone}`}>{value}</div>
      <div className="text-[11px] text-gray-400">{label}</div>
    </div>
  );
}
